/** @jsxImportSource @emotion/react */
import React from 'react'
import { css, keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import type { DivisionJob } from '../hooks/useSchedulerRequest'

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`

const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(12px); }
  100% { opacity: 1; transform: translateY(0); }
`

interface DivisionProgressCardsProps {
  divisionJobs: DivisionJob[]
}

/**
 * Status icon for each division job.
 * Completed = green check, Failed = red X, Processing/Pending = spinner/clock.
 */
const StatusIcon: React.FC<{ status: DivisionJob['status'] }> = ({
  status,
}) => {
  switch (status) {
    case 'completed':
      return (
        <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
          <circle cx='12' cy='12' r='10' fill={colors.SUCCESS_300} />
          <path
            d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z'
            fill={colors.WHITE}
          />
        </svg>
      )
    case 'failed':
      return (
        <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
          <circle cx='12' cy='12' r='10' fill={colors.ERROR_300} />
          <path
            d='M15.59 7L12 10.59 8.41 7 7 8.41 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59 13.41 12 17 8.41z'
            fill={colors.WHITE}
          />
        </svg>
      )
    case 'processing':
      return (
        <div
          css={css`
            width: 20px;
            height: 20px;
            position: relative;
          `}
        >
          <div
            css={css`
              position: absolute;
              inset: 0;
              border-radius: 50%;
              border: 2px solid transparent;
              border-top-color: ${colors.INFO_300};
              border-right-color: ${colors.INFO_300};
              animation: spin 0.8s linear infinite;

              @keyframes spin {
                0% {
                  transform: rotate(0deg);
                }
                100% {
                  transform: rotate(360deg);
                }
              }
            `}
          />
        </div>
      )
    case 'pending':
    default:
      return (
        <svg
          width='20'
          height='20'
          viewBox='0 0 24 24'
          fill='none'
          css={css`
            opacity: 0.5;
          `}
        >
          <circle
            cx='12'
            cy='12'
            r='9'
            stroke={colors.NEUTRAL_100}
            strokeWidth='2'
            fill='none'
          />
          <path
            d='M12 7V12L15 15'
            stroke={colors.NEUTRAL_100}
            strokeWidth='2'
            strokeLinecap='round'
          />
        </svg>
      )
  }
}

/**
 * Format satisfaction score for display
 */
function formatScore(score: number | null): string {
  if (score === null) {
    return ''
  }
  return `${score.toFixed(1)}%`
}

/**
 * Get the text color and label for status
 */
function getStatusDisplay(job: DivisionJob): {
  label: string
  color: string
} {
  switch (job.status) {
    case 'completed':
      return {
        label:
          job.satisfaction_score !== null
            ? formatScore(job.satisfaction_score)
            : 'completed',
        color: colors.SUCCESS_300,
      }
    case 'failed':
      return {
        label: job.error_code || 'failed',
        color: colors.ERROR_300,
      }
    case 'processing':
      return { label: 'processing...', color: colors.INFO_300 }
    case 'pending':
    default:
      return { label: 'pending', color: colors.NEUTRAL_100 }
  }
}

/**
 * Per-division progress cards shown during schedule generation.
 * Displays each division with its current status, satisfaction score,
 * and game count.
 */
const DivisionProgressCards: React.FC<DivisionProgressCardsProps> = ({
  divisionJobs,
}) => {
  const completedCount = divisionJobs.filter(
    (j) => j.status === 'completed'
  ).length
  const totalCount = divisionJobs.length
  const progressPercent =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
        max-width: 640px;
      `}
    >
      {/* Progress header */}
      {totalCount > 0 && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 4px;
          `}
        >
          <span
            css={css`
              font-family: 'Inter', sans-serif;
              font-weight: 500;
              font-size: 13px;
              color: ${colors.NEUTRAL_50};
            `}
          >
            {completedCount}/{totalCount} divisions scheduled
          </span>
          <span
            css={css`
              font-family: 'Inter', sans-serif;
              font-weight: 600;
              font-size: 13px;
              color: ${colors.INFO_300};
            `}
          >
            {progressPercent}% done
          </span>
        </div>
      )}

      {/* Progress bar */}
      {totalCount > 0 && (
        <div
          css={css`
            width: 100%;
            height: 4px;
            border-radius: 2px;
            background: rgba(56, 148, 255, 0.15);
            overflow: hidden;
            margin-bottom: 8px;
          `}
        >
          <div
            css={css`
              width: ${progressPercent}%;
              height: 100%;
              background: ${colors.INFO_300};
              border-radius: 2px;
              transition: width 0.5s ease;
            `}
          />
        </div>
      )}

      {/* Division cards */}
      {divisionJobs.map((job, index) => {
        const statusDisplay = getStatusDisplay(job)

        return (
          <div
            key={job.id}
            css={css`
              display: flex;
              flex-direction: column;
              padding: 12px 16px;
              background: ${job.status === 'completed'
                ? 'linear-gradient(135deg, rgba(16, 173, 50, 0.06) 0%, rgba(16, 173, 50, 0.02) 100%)'
                : job.status === 'failed'
                ? 'linear-gradient(135deg, rgba(217, 27, 27, 0.06) 0%, rgba(217, 27, 27, 0.02) 100%)'
                : job.status === 'processing'
                ? 'linear-gradient(135deg, rgba(56, 148, 255, 0.06) 0%, rgba(56, 148, 255, 0.02) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)'};
              border: 1px solid
                ${job.status === 'completed'
                  ? 'rgba(16, 173, 50, 0.2)'
                  : job.status === 'failed'
                  ? 'rgba(217, 27, 27, 0.2)'
                  : job.status === 'processing'
                  ? 'rgba(56, 148, 255, 0.15)'
                  : 'rgba(255, 255, 255, 0.08)'};
              border-radius: 10px;
              animation: ${fadeInUp} 0.4s ease-out ${index * 0.06}s both;
              transition: border-color 0.3s ease, background 0.3s ease;
            `}
          >
            {/* Main row */}
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 12px;
              `}
            >
              {/* Status icon */}
              <StatusIcon status={job.status} />

              {/* Division name */}
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-weight: 500;
                  font-size: 14px;
                  color: ${colors.WHITE};
                  flex: 1;
                  min-width: 0;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                `}
              >
                {job.division_name}
              </span>

              {/* Status text */}
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-weight: 400;
                  font-size: 13px;
                  color: ${statusDisplay.color};
                  flex-shrink: 0;
                  max-width: 200px;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                `}
              >
                {statusDisplay.label}
              </span>

              {/* Games count (only for completed) */}
              {job.status === 'completed' && job.games_count > 0 && (
                <span
                  css={css`
                    font-family: 'Inter', sans-serif;
                    font-weight: 400;
                    font-size: 12px;
                    color: ${colors.NEUTRAL_70};
                    flex-shrink: 0;
                    white-space: nowrap;
                  `}
                >
                  {job.games_count * 2} game
                  {job.games_count * 2 !== 1 ? 's' : ''}
                </span>
              )}

              {/* Shimmer bar for processing */}
              {job.status === 'processing' && (
                <div
                  css={css`
                    width: 32px;
                    height: 3px;
                    border-radius: 2px;
                    overflow: hidden;
                    background: rgba(56, 148, 255, 0.15);
                    flex-shrink: 0;
                  `}
                >
                  <div
                    css={css`
                      width: 100%;
                      height: 100%;
                      background: linear-gradient(
                        90deg,
                        transparent,
                        ${colors.INFO_300},
                        transparent
                      );
                      background-size: 200% 100%;
                      animation: ${shimmer} 1.5s linear infinite;
                    `}
                  />
                </div>
              )}
            </div>

            {/* Error detail row for failed jobs */}
            {job.status === 'failed' && job.error_message && (
              <div
                css={css`
                  margin-top: 8px;
                  padding: 8px 10px;
                  background: rgba(217, 27, 27, 0.06);
                  border-radius: 6px;
                  font-family: 'Inter', sans-serif;
                  font-weight: 400;
                  font-size: 12px;
                  line-height: 1.5;
                  color: ${colors.NEUTRAL_50};
                `}
              >
                {job.error_message}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default DivisionProgressCards
